import axios from 'axios';
import { ApiResponse, BrowserCaptureImportPayload, Storyboard, StoryboardStep, TemplateRecommendation, UploadProgress } from '../types';
import { API_V1_BASE_URL } from '../lib/apiBaseUrl.ts';
import { getApiAccessToken } from '../auth/accessToken.ts';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { createCorrelationId } from '../lib/correlation.ts';

const api = axios.create({
  baseURL: API_V1_BASE_URL,
  // Cloud calls (Promptflow + doc parsing) can exceed 30s, especially on cold starts.
  // Individual endpoints can still override this per-request.
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json'
  }
});

export function setCreatorAuthHeader(value: string | null) {
  if (!value) {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete api.defaults.headers.common['Authorization'];
    return;
  }
  api.defaults.headers.common['Authorization'] = value;
}

// Request interceptor (MSAL token for backend auth context)
api.interceptors.request.use(
  async (config) => {
    if (!config.headers['X-Correlation-Id']) {
      config.headers['X-Correlation-Id'] = createCorrelationId();
    }
    // If we already have an auth header (e.g. set by AuthGate), keep it.
    const existingAuth = config.headers?.['Authorization'];
    if (typeof existingAuth === 'string' && existingAuth.startsWith('Bearer ')) {
      return config;
    }

    try {
      const token = await getApiAccessToken();
      config.headers['Authorization'] = `Bearer ${token}`;
    } catch (e) {
      // If interaction is required, let the request fail with 401 and the UI will prompt login.
      if (e instanceof InteractionRequiredAuthError) {
        return config;
      }
      return config;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    return Promise.reject(error);
  }
);

function extractApiErrorMessage(error: unknown, fallback: string): string {
  if (!axios.isAxiosError(error)) return fallback;

  const payload = error.response?.data as { message?: unknown; detail?: unknown } | undefined;
  if (typeof payload?.message === 'string' && payload.message.trim()) return payload.message;
  if (typeof payload?.detail === 'string' && payload.detail.trim()) return payload.detail;
  if (Array.isArray(payload?.detail)) {
    const detail = payload.detail
      .map((entry) => (typeof entry?.msg === 'string' ? entry.msg : null))
      .filter(Boolean)
      .join(', ');
    if (detail) return detail;
  }
  return error.message || fallback;
}

export const documentApi = {
  uploadDocument: async (file: File, onProgress?: (progress: UploadProgress) => void): Promise<ApiResponse<{ documentId: string }>> => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/upload_document', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 300000,
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress({
              progress,
              status: 'uploading',
              message: `Uploading ${file.name}...`
            });
          }
        }
      });

      return response.data;
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, 'Upload failed'));
    }
  },

  uploadFromUrl: async (url: string): Promise<ApiResponse<{ documentId: string }>> => {
    const formData = new FormData();
    formData.append('url', url);

    try {
      const response = await api.post('/upload_document', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, 'URL upload failed'));
    }
  },

  importBrowserCapture: async (payload: BrowserCaptureImportPayload): Promise<ApiResponse<{ documentId: string }>> => {
    try {
      const response = await api.post('/import-browser-capture', payload);
      return response.data;
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, 'Browser capture import failed'));
    }
  },

  getDocumentSections: async (documentId: string): Promise<ApiResponse<{ sections: Array<{ title: string; preview: string }> }>> => {
    try {
      const response = await api.get(`/documents/${documentId}/sections`);
      return response.data;
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, 'Failed to inspect imported document'));
    }
  },

  deleteDocument: async (documentId: string): Promise<void> => {
    try {
      await api.delete(`/documents/${documentId}`);
    } catch {
      // Best effort cleanup only.
    }
  },

  combineDocuments: async (documentIds: string[], title?: string): Promise<ApiResponse<{ documentId: string }>> => {
    const response = await api.post('/documents/combine', {
      document_ids: documentIds,
      title
    });
    return response.data;
  },

  analyzeContent: async (documentId: string): Promise<ApiResponse<{ 
    analysis: string; 
    recommendations: TemplateRecommendation[] 
  }>> => {
    const response = await api.post('/analyze_content', null, {
      params: { document_id: documentId }
    });
    return response.data;
  }
};

export const storyboardApi = {
  generateStoryboard: async (
    documentId: string,
    templateType: string,
    preferences?: Record<string, unknown>
  ): Promise<ApiResponse<Storyboard>> => {
    const response = await api.post('/generate_storyboard', {
      documentId,
      templateType,
      preferences
    });
    return response.data;
  },

  refineStep: async (
    stepId: string,
    prompt: string,
    context: Record<string, unknown>,
    confidenceThreshold?: number
  ): Promise<ApiResponse<{ updatedStep: StoryboardStep }>> => {
    const response = await api.post('/refine_step', {
      stepId,
      prompt,
      context,
      confidenceThreshold
    });
    return response.data;
  },

  validateContent: async (storyboard: Storyboard): Promise<ApiResponse<{ 
    validationResults: Record<string, unknown>
  }>> => {
    const response = await api.post('/validate_content', { storyboard });
    return response.data;
  },

  exportStoryboard: async (
    storyboard: Storyboard,
    format: 'json' | 'pdf' | 'html'
  ): Promise<ApiResponse<{ downloadUrl: string }>> => {
    const response = await api.post('/export_storyboard', {
      storyboard,
      format
    });
    return response.data;
  }
};

export const ragApi = {
  getRagContent: async (
    query: string,
    documentId: string,
    context?: Record<string, unknown>
  ): Promise<ApiResponse<{ excerpts: Array<Record<string, unknown>> }>> => {
    const response = await api.get('/rag_content', {
      params: { query, document_id: documentId, context }
    });
    return response.data;
  }
};

export default api;

export const pfApi = {
  health: async (): Promise<{ overall: 'green'|'yellow'|'red'; statuses: Record<string,string> }> => {
    const res = await api.get('/pf-health');
    return res.data;
  }
};
